import { createStore, useSelector } from "@xstate/store-react";
import { produce } from "immer";

import { getInitialContext, statePersist } from "@/core/lib/xstate-helpers";

type LastConversation = {
  id: string | null;
  ds?: string[];
  col?: string[];
};

type Theme = "light" | "dark" | "automatic";

interface AppState {
  theme: Theme;
  lastConversation: LastConversation;
}

// Persistence functionality
const STORAGE_KEY = "app-store" as const;

const persistedKeys = ["theme"] as const;

const defaults = {
  theme: "dark",
  lastConversation: { id: null },
} satisfies Readonly<AppState>;

// Create the XState store with type-safe events
const appStore = createStore({
  context: {
    ...defaults,
    ...getInitialContext<AppState>(STORAGE_KEY, persistedKeys),
  } as AppState,
  on: {
    setTheme: (context, event: { value: Theme }) =>
      produce(context, (draft) => {
        draft.theme = event.value;
      }),
    setLastConversation: (context, event: { value: LastConversation }) =>
      produce(context, (draft) => {
        draft.lastConversation = event.value;
      }),
    reset: () => ({ ...defaults }),
  },
});

statePersist(STORAGE_KEY, appStore, persistedKeys);

export const useAppTheme = () =>
  useSelector(appStore, (state) => state.context.theme);

export const useSetAppTheme = () => (value: Theme) =>
  appStore.send({ type: "setTheme", value });

export const useLastConversation = () =>
  useSelector(appStore, (state) => state.context.lastConversation);

export { appStore };
